import { ChangeDetectionStrategy, Component, computed, input, OnInit, signal, WritableSignal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { DialogActions } from 'src/app/model/dictionary';
import { getDialogConfig } from 'src/app/util/dialog-util';
import { UserApiService } from 'src/app/data-transfer/services/user-api-service';
import { CompanyDto } from 'src/app/data-transfer/entities/doc-entities/company-dto';
import { CeoDto } from 'src/app/data-transfer/entities/doc-entities/ceo-dto';
import { SigneeDto } from 'src/app/data-transfer/entities/doc-entities/signee-dto';
import { forkJoin } from 'rxjs';
import { CountryDto } from 'src/app/data-transfer/entities/country-dto';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslocoService } from '@jsverse/transloco';
import { PpwrPersonDialogComponent, PpwrPersonDialogData } from '../../../dialogs/ppwr-person-dialog/ppwr-person-dialog.component';
import { PpwrProducerDialogComponent, PpwrProducerDialogData } from '../../../dialogs/ppwr-producer-dialog/ppwr-producer-dialog.component';
import { SignatureCaptureDialogComponent, SignatureCaptureDialogData } from '../../../dialogs/signature-capture-dialog/signature-capture-dialog.component';
import { OrganizationDto } from 'src/app/data-transfer/entities/organization-dto';
import { DocProfileDto } from 'src/app/data-transfer/entities/doc-entities/doc-profile-dto';
import { LicenseService } from 'src/app/services/licensing-service';
import { SimpleDialogData } from 'src/app/components/dialogs/simple-alert-dialog/simple-alert-dialog.component';
import { SimpleConfirmDialogComponent } from 'src/app/components/dialogs/simple-confirm-dialog/simple-confirm-dialog.component';

@Component({
  selector: 'app-ppwr-roles',
  templateUrl: './ppwr-roles.component.html',
  styleUrls: ['./ppwr-roles.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PpwrRolesComponent implements OnInit {
  readonly maxEntriesPerRole = 1000;

  readonly organizationInput = input<OrganizationDto>();
  readonly allCountriesInput = input<CountryDto[]>();
  readonly docProfilesInput = input<DocProfileDto[]>([]);

  organization: OrganizationDto = new OrganizationDto();
  readonly manufacturers = signal<CompanyDto[]>([]);
  readonly authorizedRepresentatives = signal<CompanyDto[]>([]);
  readonly ceos = signal<CeoDto[]>([]);
  readonly signees = signal<SigneeDto[]>([]);
  private readonly originalStateSnapshot = signal('');

  manufacturerSearchControl = new FormControl<string>('', { nonNullable: true });
  representativeSearchControl = new FormControl<string>('', { nonNullable: true });
  ceoSearchControl = new FormControl<string>('', { nonNullable: true });
  signeeSearchControl = new FormControl<string>('', { nonNullable: true });

  private readonly manufacturerSearch = toSignal(this.manufacturerSearchControl.valueChanges, { initialValue: '' });
  private readonly representativeSearch = toSignal(this.representativeSearchControl.valueChanges, { initialValue: '' });
  private readonly ceoSearch = toSignal(this.ceoSearchControl.valueChanges, { initialValue: '' });
  private readonly signeeSearch = toSignal(this.signeeSearchControl.valueChanges, { initialValue: '' });

  readonly filteredManufacturers = computed(() => this.filterCompanies(this.manufacturers(), this.manufacturerSearch()));
  readonly selfManufacturer = computed<CompanyDto | undefined>(() => this.manufacturers()[0]);
  readonly filteredAuthorizedRepresentatives = computed(() => this.filterCompanies(this.authorizedRepresentatives(), this.representativeSearch()));
  readonly filteredCeos = computed(() => this.filterPersons(this.ceos(), this.ceoSearch()));
  readonly filteredSignees = computed(() => this.filterPersons(this.signees(), this.signeeSearch()));
  readonly hasChanges = computed(() => this.createStateSnapshot() !== this.originalStateSnapshot());

  private deletedManufacturerIds: number[] = [];
  private deletedRepresentativeIds: number[] = [];
  private deletedCeoIds: number[] = [];
  private deletedSigneeIds: number[] = [];

  readonly isSaving = signal(false);
  allCountries: CountryDto[] = [];
  isPPWRAllowed = false;

  constructor(
    private dialog: MatDialog,
    private userApiService: UserApiService,
    private snackBar: MatSnackBar,
    private translocoService: TranslocoService,
    private licenseService: LicenseService
  ) {}

  ngOnInit(): void {
    this.allCountries = this.allCountriesInput() ?? [];
    this.setOrganizationData(this.organizationInput() ?? new OrganizationDto());

    this.isPPWRAllowed = this.licenseService.isPPWRAllowed;
  }


  openSignatureDialog<T extends CeoDto>(person: T, list: WritableSignal<T[]>): void {
    const dialogData = new SignatureCaptureDialogData(
      `${person.firstName} ${person.lastName}`,
      person.signatureImageId ?? null,
      false,
      this.translocoService.translate('createDoc.signatureCaptureDialog.saveAndUseDescription')
    );
    this.dialog
      .open(SignatureCaptureDialogComponent, this.getPpwrDialogConfig(dialogData, '680px'))
      .afterClosed()
      .subscribe(result => {
        if (result?.event !== DialogActions.CONFIRM) return;
        list.update(items => items.map(item => (item === person ? { ...item, signatureImageId: result.data.signatureImageId } : item)));
      });
  }

  openCeoDialog(): void {
    this.openPersonDialog(this.translocoService.translate('createDoc.signersDialog.managingDirectorLabel'), 'person', false, null, this.ceos);
  }

  editCeo(index: number): void {
    this.openPersonDialog(this.translocoService.translate('dataManagement.organizationSettings.dialogs.editCeo'), 'person', false, index, this.ceos);
  }

  deleteCeo(index: number): void {
    this.deletePerson(index, this.ceos, this.deletedCeoIds, 'dataManagement.organizationSettings.aria.deleteCeo');
  }

  openSigneeDialog(): void {
    this.openPersonDialog(this.translocoService.translate('createDoc.signersDialog.otherSignerTitle'), 'edit', true, null, this.signees);
  }

  editSignee(index: number): void {
    this.openPersonDialog(this.translocoService.translate('dataManagement.organizationSettings.dialogs.editSignee'), 'edit', true, index, this.signees);
  }

  deleteSignee(index: number): void {
    this.deletePerson(index, this.signees, this.deletedSigneeIds, 'dataManagement.organizationSettings.aria.deleteSignee');
  }

  openManufacturerDialog(): void {
    this.openProducerDialog(
      this.translocoService.translate('createDoc.createDocDialog.producerLabel'),
      'factory',
      null,
      this.manufacturers,
      this.docProfilesInput()
    );
  }

  editManufacturer(index: number): void {
    this.openProducerDialog(
      this.translocoService.translate('dataManagement.organizationSettings.dialogs.editManufacturer'),
      'factory',
      index,
      this.manufacturers,
      this.docProfilesInput()
    );
  }

  deleteManufacturer(index: number): void {
    this.deleteProducer(index, this.manufacturers, this.deletedManufacturerIds, 'dataManagement.organizationSettings.aria.deleteManufacturer');
  }

  openAuthorizedRepresentativeDialog(): void {
    this.openProducerDialog(
      this.translocoService.translate('createDoc.createDocDialog.representativeLabel'),
      'assignment_ind',
      null,
      this.authorizedRepresentatives,
      [],
      false
    );
  }

  editAuthorizedRepresentative(index: number): void {
    this.openProducerDialog(
      this.translocoService.translate('dataManagement.organizationSettings.dialogs.editRepresentative'),
      'assignment_ind',
      index,
      this.authorizedRepresentatives,
      [],
      false
    );
  }

  deleteAuthorizedRepresentative(index: number): void {
    this.deleteProducer(index, this.authorizedRepresentatives, this.deletedRepresentativeIds, 'dataManagement.organizationSettings.aria.deleteAuthorizedRepresentative');
  }

  private filterCompanies(list: CompanyDto[], search: string): CompanyDto[] {
    const term = search.trim().toLowerCase();
    if (!term) return list;
    return list.filter(c => c.companyName?.toLowerCase().includes(term));
  }

  private filterPersons<T extends CeoDto>(list: T[], search: string): T[] {
    const term = search.trim().toLowerCase();
    if (!term) return list;
    return list.filter(p => `${p.firstName} ${p.lastName} ${p.email}`.toLowerCase().includes(term));
  }

  saveChanges(): void {
    if (!this.hasChanges() || this.isSaving()) return;

    this.isSaving.set(true);

    const originalState = JSON.parse(this.originalStateSnapshot() || '{}');
    const requests = [...this.buildChangedCompanyRequests(originalState), ...this.buildChangedPersonRequests(originalState), ...this.buildDeleteRequests()];

    if (requests.length === 0) {
      this.isSaving.set(false);
      this.updateOriginalStateSnapshot();
      return;
    }

    forkJoin(requests).subscribe({
      next: () => {
        this.showSnackbar('dataManagement.organizationSettings.messages.saveSuccess', 'app-success', 3000);
        this.isSaving.set(false);
        this.resetDeletedIds();
        this.loadOrganization();
      },
      error: error => {
        console.error('Error while saving organization settings:', error);
        this.showSnackbar('dataManagement.organizationSettings.messages.saveError', 'app-error', 4000);
        this.isSaving.set(false);
      },
    });
  }

  private loadOrganization(): void {
    this.userApiService.getOwnOrganization(true).subscribe({
      next: result => this.setOrganizationData(result),
      error: () => this.showSnackbar('errors.serverCommunicationError', 'app-error', 4000),
    });
  }

  reloadIfUnchanged(): void {
    if (!this.hasChanges()) this.loadOrganization();
  }

  cancelChanges(): void {
    const originalState = JSON.parse(this.originalStateSnapshot() || '{}');
    this.manufacturers.set(structuredClone(originalState.manufacturers ?? []));
    this.authorizedRepresentatives.set(structuredClone(originalState.authorizedRepresentatives ?? []));
    this.ceos.set(structuredClone(originalState.ceos ?? []));
    this.signees.set(structuredClone(originalState.signees ?? []));
    this.resetDeletedIds();
  }

  private openPersonDialog<T extends CeoDto>(title: string, icon: string, showFunktion: boolean, index: number | null, list: WritableSignal<T[]>): void {
    const data = new PpwrPersonDialogData(title, icon, showFunktion, index !== null ? list()[index] : undefined);
    this.dialog
      .open(PpwrPersonDialogComponent, this.getPpwrDialogConfig(data, '680px'))
      .afterClosed()
      .subscribe(result => {
        if (result?.event !== DialogActions.CONFIRM) return;
        if (index !== null) {
          list.update(items => items.map((item, i) => (i === index ? { ...item, ...result.data } : item)));
        } else {
          list.update(items => [...items, { personProfileId: -1, ...result.data } as T]);
        }
      });
  }

  private openProducerDialog(
    title: string,
    icon: string,
    index: number | null,
    list: WritableSignal<CompanyDto[]>,
    docProfiles: DocProfileDto[] = [],
    showDocProfileField = true
  ): void {
    const data = new PpwrProducerDialogData(title, icon, this.allCountries, index !== null ? list()[index] : undefined, docProfiles, showDocProfileField);
    this.dialog
      .open(PpwrProducerDialogComponent, this.getPpwrDialogConfig(data, '720px'))
      .afterClosed()
      .subscribe(result => {
        if (result?.event !== DialogActions.CONFIRM) return;
        if (index !== null) {
          list.update(items => items.map((item, i) => (i === index ? { ...item, ...result.data } : item)));
        } else {
          list.update(items => [...items, { companyProfileId: -1, ...result.data }]);
        }
      });
  }

  private deletePerson<T extends CeoDto>(index: number, list: WritableSignal<T[]>, deletedIds: number[], titleKey: string): void {
    const item = list()[index];
    if (!item) return;
    this.confirmDelete(titleKey, `${item.firstName} ${item.lastName}`, () => {
      if (item.personProfileId > 0) deletedIds.push(item.personProfileId);
      list.update(items => items.filter(existing => existing !== item));
    });
  }

  private deleteProducer(index: number, list: WritableSignal<CompanyDto[]>, deletedIds: number[], titleKey: string): void {
    const item = list()[index];
    if (!item || item === this.selfManufacturer()) return;
    this.confirmDelete(titleKey, item.companyName ?? '', () => {
      if (item.companyProfileId > 0) deletedIds.push(item.companyProfileId);
      list.update(items => items.filter(existing => existing !== item));
    });
  }

  private confirmDelete(titleKey: string, name: string, onConfirm: () => void): void {
    const dialogData: SimpleDialogData = {
      title: this.translocoService.translate(titleKey),
      messages: [this.translocoService.translate('dataManagement.organizationSettings.messages.deleteConfirm', { name })],
      icon: 'delete',
    };
    this.dialog
      .open(SimpleConfirmDialogComponent, getDialogConfig(dialogData, '480px'))
      .afterClosed()
      .subscribe(result => {
        if (result?.event === DialogActions.CONFIRM) onConfirm();
      });
  }

  private buildChangedCompanyRequests(originalState: any) {
    const requests = [];
    const changedManufacturers = this.getChangedItems(this.manufacturers(), originalState.manufacturers ?? [], 'companyProfileId');
    const changedRepresentatives = this.getChangedItems(this.authorizedRepresentatives(), originalState.authorizedRepresentatives ?? [], 'companyProfileId');
    if (changedManufacturers.length > 0) requests.push(this.userApiService.createOrUpdateManufacturerProfiles(changedManufacturers, true));
    if (changedRepresentatives.length > 0) requests.push(this.userApiService.createOrUpdateRepresentativeProfiles(changedRepresentatives, true));
    return requests;
  }

  private buildChangedPersonRequests(originalState: any) {
    const requests = [];
    const changedCeos = this.getChangedItems(this.ceos(), originalState.ceos ?? [], 'personProfileId');
    const changedSignees = this.getChangedItems(this.signees(), originalState.signees ?? [], 'personProfileId');
    if (changedCeos.length > 0) requests.push(this.userApiService.createOrUpdateCeoProfiles(changedCeos, true));
    if (changedSignees.length > 0) requests.push(this.userApiService.createOrUpdateSigneeProfiles(changedSignees, true));
    return requests;
  }

  private buildDeleteRequests() {
    const requests = [];
    if (this.deletedManufacturerIds.length > 0) requests.push(this.userApiService.deleteManufacturerProfiles(this.deletedManufacturerIds, true));
    if (this.deletedRepresentativeIds.length > 0) requests.push(this.userApiService.deleteRepresentativeProfiles(this.deletedRepresentativeIds, true));
    if (this.deletedCeoIds.length > 0) requests.push(this.userApiService.deleteCeoProfiles(this.deletedCeoIds, true));
    if (this.deletedSigneeIds.length > 0) requests.push(this.userApiService.deleteSigneeProfiles(this.deletedSigneeIds, true));
    return requests;
  }

  private getChangedItems<T>(current: T[], original: T[], idKey: keyof T): T[] {
    return current.filter(item => {
      if ((item[idKey] as number) < 0) return true;
      const originalItem = original.find((o: T) => o[idKey] === item[idKey]);
      return !originalItem || JSON.stringify(item) !== JSON.stringify(originalItem);
    });
  }

  private setOrganizationData(result: OrganizationDto): void {
    this.organization = result;
    this.manufacturers.set([...(result.manufacturerProfiles ?? [])].sort((a, b) => a.companyProfileId - b.companyProfileId));
    this.authorizedRepresentatives.set([...(result.authorizedRepresentativeProfiles ?? [])].sort((a, b) => a.companyProfileId - b.companyProfileId));
    this.ceos.set([...(result.ceoProfiles ?? [])].sort((a, b) => a.personProfileId - b.personProfileId));
    this.signees.set([...(result.signeeProfiles ?? [])].sort((a, b) => a.personProfileId - b.personProfileId));
    this.updateOriginalStateSnapshot();
    this.resetDeletedIds();
  }

  private updateOriginalStateSnapshot(): void {
    this.originalStateSnapshot.set(this.createStateSnapshot());
  }

  private createStateSnapshot(): string {
    return JSON.stringify({
      manufacturers: this.manufacturers(),
      authorizedRepresentatives: this.authorizedRepresentatives(),
      ceos: this.ceos(),
      signees: this.signees(),
    });
  }

  private showSnackbar(messageKey: string, panelClass: string, duration: number): void {
    this.snackBar.open(this.translocoService.translate(messageKey), this.translocoService.translate('common.buttons.close'), {
      duration,
      panelClass: ['app-snackbar', panelClass],
    });
  }

  private getPpwrDialogConfig(data: unknown, width: string): MatDialogConfig {
    const config = getDialogConfig(data, width);
    config.maxWidth = 'calc(100vw - 32px)';
    config.panelClass = 'ppwr-dialog-panel';
    config.restoreFocus = true;
    return config;
  }

  private resetDeletedIds(): void {
    this.deletedManufacturerIds = [];
    this.deletedRepresentativeIds = [];
    this.deletedCeoIds = [];
    this.deletedSigneeIds = [];
  }
}
